// The Events panel (rail › Events): the marina's own calendar, kept in /events. Everyone on staff
// sees it, live; admin and office add, change and delete (database.rules.json enforces the same).
// Date rules live in eventsCore.js. Replaced the Google Calendar panel (v0.4.449).

import { ref, onValue, get, push as sdkPush } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, database } from './initFirebase';
import { set, update, remove } from './trackedDb';
import { applyLive, liveRefresher, patchHTML, recentInteraction } from './liveView';
import { escapeHTML } from './utilities';
import { formatDateToYYYYMMDD } from './date-format';
import {
  EVENT_CATEGORIES, REPEATS, occurrences, groupByDay, countOn, trimRepeats, countEvents, whenLabel, timeLabel, repeatLabel, categoryLabel,
  eventFromForm, changedEventFields, addDays, daysBetween, compareOccurrences,
} from './eventsCore';

const LOOK_AHEAD_DAYS = 120;
const esc = escapeHTML;

let events = {};
let loaded = false;
let loadError = '';
let canEdit = false;
let me = null;
// What the panel shows: { name: 'list' } | { name: 'detail', id, day } | { name: 'edit', id, day, before }
let view = { name: 'list' };
let stopListening = null;
let panel, body, redraw;

// Other parts of the app can add read-only occurrences later (hauls, time off…): fn(from, to) → list
// in eventsCore's occurrence shape, with their own `source`.
const sources = [];
export function registerEventSource(fn) { sources.push(fn); redraw?.(); }

function today() { return formatDateToYYYYMMDD(new Date()); }

function upcoming(from = today()) {
  const to = addDays(from, LOOK_AHEAD_DAYS);
  const extra = sources.flatMap(fn => { try { return fn(from, to) || []; } catch { return []; } });
  return occurrences(events, from, to).concat(extra).sort(compareOccurrences);
}

function dayLabel(day, opts = { weekday: 'short', month: 'short', day: 'numeric' }) {
  return new Date(`${day}T12:00:00`).toLocaleDateString('en-US', opts);
}

// ---------- rail count ----------

function updateRailCount() {
  const el = document.getElementById('rail-events-count');
  if (!el) return;
  const n = loaded ? countOn(upcoming(), today()) : 0;
  // Only when there's something today: "0 today" is noise in the glance strip.
  el.textContent = n ? `${n} today` : '';
  el.style.display = n ? 'inline-block' : 'none';
}

// ---------- views ----------

function setStatus() {
  const el = document.getElementById('calendar-status-text');
  const dot = document.getElementById('calendar-status-dot');
  const n = countEvents(upcoming());
  if (el) el.textContent = loadError ? 'Can\'t load events' : !loaded ? 'Loading…' : n ? `Marina calendar · ${n} upcoming` : 'Marina calendar';
  dot?.classList.toggle('calendar-status-dot-connected', loaded && !loadError);
  const add = document.getElementById('events-add-btn');
  if (add) add.hidden = !canEdit || view.name === 'edit';
}

function listHTML() {
  if (loadError) return `<div class="calendar-empty"><div class="calendar-empty-title">Can't load events</div><div class="calendar-empty-body">${esc(loadError)}</div></div>`;
  if (!loaded) return '<div class="calendar-empty"><div class="calendar-empty-body">Loading…</div></div>';
  const from = today();
  const groups = groupByDay(trimRepeats(upcoming(from), from), from);
  if (!groups.length) {
    return `<div class="calendar-empty">
      <div class="calendar-empty-title">No upcoming events</div>
      <div class="calendar-empty-body">${canEdit ? 'Add the marina\'s events (parties, races, closures, staff meetings) and everyone on staff sees them here.' : 'Nothing on the marina calendar in the next few months.'}</div>
      ${canEdit ? '<button type="button" class="calendar-reconnect-btn" data-act="add">Add an event</button>' : ''}
    </div>`;
  }
  return groups.map(([day, list]) => `
    <div class="date-section${day === from ? ' date-section-today' : ''}" data-key="day-${day}">
      <div class="date-section-header">
        <span class="date-section-label">${esc(dayLabel(day).toUpperCase())}</span>
        ${day === from ? '<span class="date-section-today-tag">TODAY</span>' : ''}
        <span class="date-section-hairline"></span>
      </div>
      ${list.map(o => itemHTML(o, day)).join('')}
    </div>`).join('');
}

function itemHTML(o, shownOn) {
  const meta = [
    o.lastDay !== o.day ? (o.day < shownOn ? `Day ${daysBetween(o.day, shownOn) + 1}, until ${dayLabel(o.lastDay, { month: 'short', day: 'numeric' })}` : `Until ${dayLabel(o.lastDay, { weekday: 'short', month: 'short', day: 'numeric' })}`) : '',
    o.location,
    o.repeat !== 'none' ? '↻ Repeats' : '',
  ].filter(Boolean).join(' · ');
  return `<button type="button" class="event-item event-cat-${esc(o.category)}" data-key="${esc(o.key)}" data-act="open" data-id="${esc(o.id)}" data-day="${o.day}" data-source="${esc(o.source)}">
    <span class="event-item-chip ${o.allDay ? 'event-item-chip-allday' : 'event-item-chip-timed'}">${esc(whenLabel(o))}</span>
    <span class="event-item-text">
      <span class="event-item-title">${esc(o.title)}</span>
      ${meta ? `<span class="event-item-meta">${esc(meta)}</span>` : ''}
    </span>
  </button>`;
}

function detailHTML() {
  const ev = events[view.id];
  if (!ev) {
    return `<div class="events-view">
      <button type="button" class="events-back" data-act="back">‹ All events</button>
      <div class="events-notice">Someone else deleted this event.</div>
    </div>`;
  }
  const o = occurrences({ [view.id]: ev }, view.day, view.day)[0];
  const when = [
    dayLabel(view.day, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
    o && o.lastDay !== o.day ? `through ${dayLabel(o.lastDay, { weekday: 'long', month: 'long', day: 'numeric' })}` : '',
  ].filter(Boolean).join(' ');
  const time = ev.allDay ? 'All day' : [timeLabel(ev.start), timeLabel(ev.end)].filter(Boolean).join(' – ');
  const repeats = repeatLabel(ev);
  const rows = [
    ['When', `${when}<br>${esc(time)}`],
    repeats ? ['Repeats', esc(repeats)] : null,
    ['Type', esc(categoryLabel(ev.category))],
    ev.location ? ['Where', esc(ev.location)] : null,
    ev.notes ? ['Notes', esc(ev.notes).replace(/\n/g, '<br>')] : null,
    ev.createdByName ? ['Added by', esc(ev.createdByName)] : null,
  ].filter(Boolean);
  const isRepeat = (ev.repeat || 'none') !== 'none';
  return `<div class="events-view" data-key="detail-${esc(view.id)}">
    <button type="button" class="events-back" data-act="back">‹ All events</button>
    <h3 class="events-detail-title event-cat-${esc(ev.category || 'marina')}">${esc(ev.title)}</h3>
    <dl class="events-detail">${rows.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join('')}</dl>
    ${canEdit ? `<div class="events-actions">
      <button type="button" class="events-btn events-btn-primary" data-act="edit">Edit</button>
      ${isRepeat ? '<button type="button" class="events-btn" data-act="skip" data-confirm="Skip only this date?">Skip this date</button>' : ''}
      <button type="button" class="events-btn events-btn-danger" data-act="delete" data-confirm="${isRepeat ? 'Delete every date?' : 'Delete this event?'}">${isRepeat ? 'Delete all dates' : 'Delete'}</button>
    </div>
    ${isRepeat ? '<p class="events-hint">Edit changes every date of this event.</p>' : ''}` : ''}
  </div>`;
}

function formHTML() {
  const ev = view.before || {};
  const v = (f, d = '') => esc(ev[f] ?? d);
  const allDay = view.id ? !!ev.allDay : false;
  return `<form class="events-view events-form" novalidate data-live-ok>
    <button type="button" class="events-back" data-act="back">‹ ${view.id ? 'Back' : 'All events'}</button>
    <h3 class="events-form-title">${view.id ? 'Edit event' : 'New event'}</h3>
    <div class="events-notice" id="events-form-gone" hidden>Someone else deleted this event while you were editing. Saving adds it again.</div>
    <label class="events-field"><span>Name</span><input name="title" maxlength="120" required value="${v('title')}" placeholder="e.g. Haul-out party"></label>
    <label class="events-check"><input type="checkbox" name="allDay"${allDay ? ' checked' : ''}> All day</label>
    <div class="events-row">
      <label class="events-field"><span>${allDay ? 'First day' : 'Date'}</span><input type="date" name="date" required value="${v('date', view.day || today())}"></label>
      <label class="events-field events-allday-only"${allDay ? '' : ' hidden'}><span>Last day</span><input type="date" name="endDate" value="${v('endDate')}"></label>
    </div>
    <div class="events-row events-timed-only"${allDay ? ' hidden' : ''}>
      <label class="events-field"><span>Starts</span><input type="time" name="start" value="${v('start', '09:00')}"></label>
      <label class="events-field"><span>Ends</span><input type="time" name="end" value="${v('end')}"></label>
    </div>
    <div class="events-row">
      <label class="events-field"><span>Repeats</span><select name="repeat">${REPEATS.map(r => `<option value="${r.id}"${(ev.repeat || 'none') === r.id ? ' selected' : ''}>${r.label}</option>`).join('')}</select></label>
      <label class="events-field events-repeat-only"${(ev.repeat || 'none') === 'none' ? ' hidden' : ''}><span>Until (optional)</span><input type="date" name="repeatUntil" value="${v('repeatUntil')}"></label>
    </div>
    <label class="events-field"><span>Type</span><select name="category">${EVENT_CATEGORIES.map(c => `<option value="${c.id}"${(ev.category || 'marina') === c.id ? ' selected' : ''}>${c.label}</option>`).join('')}</select></label>
    <label class="events-field"><span>Where (optional)</span><input name="location" maxlength="200" value="${v('location')}" placeholder="e.g. Clubhouse"></label>
    <label class="events-field"><span>Notes (optional)</span><textarea name="notes" rows="3" maxlength="2000">${v('notes')}</textarea></label>
    <div class="events-error" id="events-form-error" role="alert"></div>
    <div class="events-actions">
      <button type="submit" class="events-btn events-btn-primary">${view.id ? 'Save' : 'Add event'}</button>
      <button type="button" class="events-btn" data-act="back">Cancel</button>
    </div>
  </form>`;
}

function draw() {
  if (!body) return;
  setStatus();
  updateRailCount();
  if (view.name === 'list') patchHTML(body, listHTML());
  else if (view.name === 'detail') patchHTML(body, detailHTML());
}

function show(next) {
  view = next;
  setStatus();
  if (view.name === 'edit') body.innerHTML = formHTML();
  else draw();
  body.scrollTop = 0;
  if (view.name === 'edit') body.querySelector('input[name="title"]')?.focus();
}

// ---------- actions ----------

function onBodyClick(e) {
  const btn = e.target.closest('[data-act]');
  if (!btn || !body.contains(btn)) return;
  const act = btn.dataset.act;
  // Destructive buttons ask once, in place: the first click changes the label, the second acts.
  if (btn.dataset.confirm && !btn.dataset.armed) {
    btn.dataset.armed = '1';
    btn.dataset.label = btn.textContent;
    btn.textContent = btn.dataset.confirm;
    setTimeout(() => { if (btn.isConnected && btn.dataset.armed) { delete btn.dataset.armed; btn.textContent = btn.dataset.label; } }, 4000);
    return;
  }
  if (act === 'add') return show({ name: 'edit', id: null, day: today(), before: null });
  if (act === 'back') return show(view.name === 'edit' && view.id && events[view.id] ? { name: 'detail', id: view.id, day: view.day } : { name: 'list' });
  if (act === 'open') {
    if (btn.dataset.source !== 'events') return; // other sources open their own pages (later)
    return show({ name: 'detail', id: btn.dataset.id, day: btn.dataset.day });
  }
  if (!canEdit) return;
  const id = view.id;
  if (act === 'edit') return show({ name: 'edit', id, day: view.day, before: { ...events[id] } });
  if (act === 'skip') {
    background(update(ref(database, `events/${id}`), { [`skip/${view.day}`]: true, ...stamp() }));
    return show({ name: 'list' });
  }
  if (act === 'delete') {
    background(remove(ref(database, `events/${id}`)));
    return show({ name: 'list' });
  }
}

function stamp() {
  return { updatedBy: me?.uid || '', updatedAt: Date.now() };
}

// Writes show at once (Firebase applies them locally) and finish in the background: trackedDb keeps
// them on the device until the server has them, and the sync chip shows any still waiting or refused.
function background(write) {
  write.catch(err => {
    console.error('[events] save failed', err);
    if (/permission/i.test(String(err?.message || err))) window.alert('Only admin and office can change events.');
  });
}

function onSubmit(e) {
  e.preventDefault();
  const form = e.target;
  const data = Object.fromEntries(new FormData(form).entries());
  data.allDay = form.allDay.checked;
  const rec = eventFromForm(data);
  const errEl = form.querySelector('#events-form-error');
  if (rec.error) { errEl.textContent = rec.error; return; }
  errEl.textContent = '';
  let id = view.id;
  if (id && events[id]) {
    // Send only what this person changed, so someone else's edit to another field survives.
    const changes = changedEventFields(view.before, rec);
    if (Object.keys(changes).length) background(update(ref(database, `events/${id}`), { ...changes, ...stamp() }));
  } else {
    id = id || sdkPush(ref(database, 'events')).key;
    const now = Date.now();
    background(set(ref(database, `events/${id}`), {
      ...rec, createdBy: me?.uid || '', createdByName: me?.name || '', createdAt: now, updatedBy: me?.uid || '', updatedAt: now,
    }));
  }
  show({ name: 'detail', id, day: rec.date >= today() || rec.repeat === 'none' ? rec.date : firstUpcoming(id) || rec.date });
}

function firstUpcoming(id) {
  return occurrences({ [id]: events[id] }, today(), addDays(today(), 400))[0]?.day;
}

function onFormInput(e) {
  const form = e.target.closest('form');
  if (!form) return;
  if (e.target.name === 'allDay') {
    const on = e.target.checked;
    form.querySelectorAll('.events-allday-only').forEach(el => { el.hidden = !on; });
    form.querySelectorAll('.events-timed-only').forEach(el => { el.hidden = on; });
    form.querySelector('input[name="date"]').previousElementSibling.textContent = on ? 'First day' : 'Date';
  }
  if (e.target.name === 'repeat') {
    form.querySelectorAll('.events-repeat-only').forEach(el => { el.hidden = e.target.value === 'none'; });
  }
}

// ---------- data ----------

function listen() {
  stopListening?.();
  stopListening = onValue(ref(database, 'events'), snap => {
    events = snap.val() || {};
    loaded = true;
    loadError = '';
    // The record being edited was deleted: say so (Save adds it back) instead of redrawing the form.
    if (view.name === 'edit' && view.id) {
      const gone = body?.querySelector('#events-form-gone');
      if (gone) gone.hidden = !!events[view.id];
    }
    updateRailCount();
    redraw();
  }, err => {
    console.error('[events] listen failed', err);
    loadError = /permission/i.test(err?.message || '') ? 'This account can\'t see the calendar.' : 'Check the connection.';
    redraw();
  });
}

export function initEvents() {
  panel = document.getElementById('calendar-pop-out-box');
  body = document.getElementById('eventsContainer');
  if (!panel || !body || body.dataset.wired) return;
  body.dataset.wired = '1';

  redraw = liveRefresher({
    refresh: () => { if (view.name !== 'edit') applyLive(draw, { glow: !recentInteraction() && panel.classList.contains('open') }); },
    // Also wait while a Delete/Skip is armed: a redraw would reset it and the second click would only ask again.
    busy: () => view.name === 'edit' || !!body.querySelector('[data-armed]'),
  });

  body.addEventListener('click', onBodyClick);
  body.addEventListener('submit', onSubmit);
  body.addEventListener('change', onFormInput);
  document.getElementById('events-add-btn')?.addEventListener('click', () => show({ name: 'edit', id: null, day: today(), before: null }));
  document.getElementById('calendar-open-btn')?.addEventListener('click', () => {
    const open = panel.classList.toggle('open');
    if (open && view.name !== 'edit') show({ name: 'list' });
  });
  document.getElementById('calendar-close-btn')?.addEventListener('click', () => panel.classList.remove('open'));

  // The day turns over while the app is open: today's count and the TODAY tag move with it.
  let shownDay = today();
  setInterval(() => { if (today() !== shownDay) { shownDay = today(); redraw(); } }, 60000);

  onAuthStateChanged(auth, async user => {
    stopListening?.();
    stopListening = null;
    events = {};
    loaded = false;
    canEdit = false;
    me = null;
    if (!user) { updateRailCount(); return; }
    me = { uid: user.uid, name: user.displayName || user.email || '' };
    try {
      const role = String((await get(ref(database, `users/${user.uid}/role`))).val() || '');
      const roles = role.split(',').map(s => s.trim().toLowerCase());
      canEdit = roles.includes('admin') || roles.includes('office');
      if (!role) return; // no role: nothing to show (the app's own no-role notice takes over)
    } catch { /* offline: still listen, the cached copy may come through */ }
    listen();
    draw();
  });
}
